import {
  createTheme,
  PaletteOptions,
  Theme,
  ThemeOptions,
  ThemeProvider,
  useMediaQuery,
  alpha,
} from '@mui/material';
import Color from 'color';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import CssBaseline from '@mui/material/CssBaseline';

declare module '@mui/material/styles' {
  interface Palette {
    tertiary?: Palette['primary'];
    backdrop?: { overlay: string };

    calendar1: Palette['primary'];
    calendar2: Palette['primary'];
    calendar3: Palette['primary'];
  }
  interface PaletteOptions {
    tertiary?: PaletteOptions['primary'];
    calendar1?: PaletteOptions['primary'];
    calendar2?: PaletteOptions['primary'];
    calendar3?: PaletteOptions['primary'];
    backdrop?: { overlay: string };
  }
}

const calendarColors = ['#4b85ff', '#ff9b09', '#ff5670'].reduce(
  (acc, color, idx) => ({
    ...acc,
    [`calendar${idx + 1}`]: {
      main: color,
      light: alpha(color, 0.5),
      dark: alpha(color, 0.9),
      contrastText: '#fff',
    },
  }),
  {} as Partial<PaletteOptions>,
);

const themeDefaults: Omit<ThemeOptions, 'palette'> = {
  typography: {
    fontFamily: "'Figtree', Arial, sans-serif",
    button: { letterSpacing: '0.46px' },
    allVariants: { color: 'text.primary' },
    h1: { fontFamily: "'Poppins', serif", fontSize: '4rem' },
    h2: { fontFamily: "'Poppins', serif", fontSize: '3rem' },
    h3: { fontFamily: "'Poppins', serif", fontSize: '2rem' },
    h4: { fontFamily: "'Poppins', serif", fontSize: '1.5rem' },
    h5: { fontFamily: "'Poppins', serif", fontSize: '1.3rem' },
    h6: { fontSize: '1.2rem', fontWeight: 700 },
    overline: { fontWeight: 400, letterSpacing: '0.1em' },
  },
  components: {
    // Add print-specific overrides
    MuiCssBaseline: {
      styleOverrides: {
        '@media print': {
          // Force light theme colors for print
          body: {
            backgroundColor: '#ffffff !important',
            color: '#2A2A2A !important',
          },
          // Override any dark theme elements
          '*': {
            backgroundColor: 'transparent !important',
            color: '#2A2A2A !important',
            boxShadow: 'none !important',
          },
          // Ensure primary colors are print-friendly
          '.MuiButton-root, .MuiChip-root': {
            backgroundColor: '#537f68 !important',
            color: '#ffffff !important',
          },
          '.MuiContainer-root': {
            ml: 0,
            mr: 0,
            maxWidth: 'unset !important',
          },
          '.MuiAppBar-root': {
            display: 'none !important',
          },
        },
      },
    },
    MuiTextField: { defaultProps: { fullWidth: true } },
    MuiPaper: { styleOverrides: { root: { color: 'text.disabled' } } },
    MuiTab: {
      styleOverrides: { root: { textTransform: 'none', fontWeight: 400 } },
    },
    MuiButton: { styleOverrides: { root: { textTransform: 'none' } } },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { borderRadius: '10px' } },
    },
    MuiCardHeader: { styleOverrides: { action: { marginLeft: 8 } } },
    MuiMenu: {
      defaultProps: { BackdropProps: { sx: { background: 'none' } } },
    },
    MuiBackdrop: {
      styleOverrides: { root: { backgroundColor: 'backdrop.overlay' } },
    },
    MuiAccordion: {
      styleOverrides: { root: { '&:before': { display: 'none' } } },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          width: '40px',
          height: '40px',
          backgroundColor: 'primary.light',
          color: 'primary.contrastText',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: { root: { backgroundColor: 'background.input' } },
    },
    MuiTableCell: {
      styleOverrides: { root: { background: 'none !important' } },
    },
    MuiTabs: {
      styleOverrides: { root: { backgroundColor: 'background.paper' } },
    },
    MuiCircularProgress: {
      styleOverrides: { root: { color: 'primary.main' } },
    },
    MuiDialogTitle: { defaultProps: { variant: 'h5' } },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'underline',
          fontWeight: 'bold',
          color: 'primary.main',
        },
      },
    },
    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: { root: { '&::after': { animationDuration: '1.5s' } } },
    },
    MuiDivider: {
      styleOverrides: { root: { opacity: 1, borderColor: 'divider' } },
    },
    MuiAlert: {
      defaultProps: { icon: false, variant: 'filled' },
      styleOverrides: {
        // Display the icon as absolute so it doesn't take up space, then we can add a margin to the alert title
        root: ({ ownerState: { icon = false }, theme }) => ({
          background:
            theme.palette.mode === 'dark'
              ? `linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3))`
              : `linear-gradient(0deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.3))`,
          '& .MuiAlertTitle-root': { ...(icon ? { marginLeft: 40 } : {}) },
        }),
        filledError: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.error.dark
              : theme.palette.error.light,
          color: theme.palette.text.primary,
        }),
        filledInfo: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.info.dark
              : theme.palette.info.light,
          color: theme.palette.text.primary,
        }),
        filledSuccess: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.success.dark
              : theme.palette.success.light,
          color: theme.palette.text.primary,
        }),
        filledWarning: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.warning.dark
              : theme.palette.warning.light,
          color: theme.palette.text.primary,
        }),
        message: { width: '100%' },
        icon: { position: 'absolute' },
      },
    },
  },
};

export const lightTheme: PaletteOptions = {
  mode: 'light',
  primary: { main: '#537f68' },
  secondary: { main: '#536A7F' },
  tertiary: { main: '#AABEBF' },
  error: { main: '#c74b4b' },
  warning: { main: '#E1B16A' },
  info: { main: '#A0C4FF' },
  success: { main: '#006f35ff' },
  text: {
    primary: '#2A2A2A',
    secondary: 'rgba(42, 42, 42, 0.8)',
    disabled: 'rgba(42, 42, 42, 0.6)',
  },
  backdrop: { overlay: 'rgb(106, 142, 127)' },
  background: { default: '#f6f2ee', paper: '#fcf7f8' },
  ...calendarColors,
};

export const darkTheme: PaletteOptions = {
  mode: 'dark',
  primary: {
    // main: '#537f68',
    main: Color('#537f68').lighten(0.2).hex(),
  },
  secondary: { main: Color('#536A7F').lighten(0.2).hex() },
  tertiary: { main: Color('#AABEBF').lighten(0.2).hex() },
  error: { main: '#c74b4b' },
  warning: { main: '#E1B16A' },
  info: { main: '#A0C4FF' },
  success: { main: '#006f35ff' },
  text: {
    primary: '#E0E0E0',
    secondary: 'rgba(240, 240, 240, 0.9)',
    disabled: 'rgba(240, 240, 240, 0.7)',
  },
  backdrop: { overlay: 'rgba(0, 0, 0, 0.5)' },
  background: { default: '#3E3E3E', paper: '#333' },
  ...calendarColors,
};

const generateTheme = (mode: 'light' | 'dark', sidebarWidth: number): Theme => {
  const palette: PaletteOptions = mode === 'light' ? lightTheme : darkTheme;

  return createTheme({
    ...themeDefaults,
    palette,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600 + sidebarWidth,
        md: 900 + sidebarWidth,
        lg: 1200 + sidebarWidth,
        xl: 1546 + sidebarWidth,
      },
    },
  });
};

function CreateMuiSwitchableThemeProvider() {
  const Context = createContext({
    setMuiTheme: (theme: 'light' | 'dark') => {},
    navMode: 'left',
    setNavMode: (mode: 'top' | 'left') => {},
    navCollapsed: false,
    setNavCollapsed: (collapsed: boolean) => {},
    themeMode: 'light',
    marginLeft: 0,
    marginTop: 0,
    navWidth: 0,
    isTablet: false,
  });

  const { Provider } = Context;

  const MuiSwitchableThemeProvider = ({
    children,
    forceTheme,
  }: {
    children: ReactNode;
    forceTheme?: 'light' | 'dark'; // This is a way for us to force a theme, useful for Storybook
  }) => {
    const localNavMode =
      typeof window !== 'undefined' && localStorage.getItem('navMode') === 'top'
        ? 'top'
        : 'left';
    const localTheme =
      typeof window !== 'undefined' &&
      localStorage.getItem('themeMode') === 'dark'
        ? 'dark'
        : 'light';
    const [theme, setTheme] = useState<Theme>(
      generateTheme(forceTheme ?? localTheme, 0),
    );
    const [themeMode, setThemeMode] = useState<'light' | 'dark'>(
      forceTheme ?? localTheme,
    );

    const [navMode, setNavModeInternal] = useState<'top' | 'left'>(
      localNavMode,
    );
    const [navCollapsed, setNavCollapsed] = useState(navMode === 'top');

    const isTablet = useMediaQuery('(max-width: 900px)');

    const setNavMode = (mode: 'top' | 'left', saveToLocalStorage = true) => {
      setNavModeInternal(mode);
      if (saveToLocalStorage && typeof window !== 'undefined') {
        localStorage.setItem('navMode', mode);
      }
    };

    // Update
    useEffect(() => {
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute(
          'content',
          theme.palette.mode === 'dark'
            ? theme.palette.primary.dark
            : theme.palette.primary.main,
        );
    }, [theme]);

    useEffect(() => {
      if (forceTheme) {
        setThemeMode(forceTheme);
      }
    }, [forceTheme]);

    useEffect(() => {
      if (isTablet) {
        setNavMode('top', false);
        setNavCollapsed(true);
        return;
      }

      setNavMode(localNavMode, false);
    }, [isTablet]);

    useEffect(() => {
      document
        .getElementById('navbar')
        ?.setAttribute('data-bs-theme', themeMode);
      document.documentElement.setAttribute('data-bs-theme', themeMode);
      if (typeof window !== 'undefined') {
        localStorage.setItem('themeMode', themeMode);
      }
    }, [themeMode]);

    const marginLeft = useMemo(() => {
      if (navMode === 'top') return 0;

      return navCollapsed ? 64 : 240;
    }, [navMode, navCollapsed]);

    const marginTop = useMemo(() => (navMode === 'top' ? 64 : 0), [navMode]);

    const navWidth = useMemo(() => {
      if (navMode === 'top') return navCollapsed ? 0 : 240;
      return navCollapsed ? 64 : 240;
    }, [navMode, navCollapsed]);

    useEffect(() => {
      setTheme(generateTheme(themeMode, marginLeft));
    }, [themeMode, marginLeft]);

    return (
      <Provider
        value={{
          setMuiTheme: setThemeMode,
          themeMode,
          navMode,
          setNavMode,
          navCollapsed,
          setNavCollapsed,
          marginLeft,
          marginTop,
          navWidth,
          isTablet,
        }}
      >
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LocalizationProvider dateAdapter={AdapterMoment}>
            {children}
          </LocalizationProvider>
        </ThemeProvider>
      </Provider>
    );
  };

  const useMuiSwitchableTheme = () => {
    const context = useContext(Context);

    if (!context) {
      throw new Error(
        'useMuiSwitchableTheme must be used within a MuiSwitchableThemeProvider',
      );
    }

    return context;
  };

  return { MuiSwitchableThemeProvider, useMuiSwitchableTheme };
}

const { MuiSwitchableThemeProvider, useMuiSwitchableTheme } =
  CreateMuiSwitchableThemeProvider();

export { MuiSwitchableThemeProvider, useMuiSwitchableTheme };
